import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./InfoModal.css";
import { Info, X, ArrowRight } from "lucide-react";

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  showContinueButton?: boolean;
  continueButtonText?: string;
  onContinueClick?: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({
  isOpen,
  onClose,
  title = "Information",
  message,
  showContinueButton = false,
  continueButtonText = "Continue",
  onContinueClick,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      if (modalRef.current) modalRef.current.focus();
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleContinue = () => {
    if (onContinueClick) {
      onContinueClick();
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="info-modal-overlay"
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
    >
      <div className="info-modal-content" ref={modalRef}>
        <button className="info-modal-close" onClick={onClose} title="Close">
          <X className="w-5 h-5" aria-label="Close modal" />
        </button>
        
        <div className="info-modal-icon">
          <Info className="w-20 h-20" />
        </div>
        
        <div className="info-modal-body">
          <h2 className="info-modal-title">{title}</h2>
          <p className="info-modal-message">{message}</p>
        </div>
        
        <div className="info-modal-footer">
          {showContinueButton ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <button 
                className="info-modal-button info-modal-continue-button" 
                onClick={handleContinue}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {continueButtonText}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button 
                className="info-modal-button" 
                onClick={onClose}
                style={{
                  background: '#6b7280'
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button className="info-modal-button" onClick={onClose}>
              OK
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InfoModal;
